/** @shell standard @header bar */
import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import Animated from 'react-native-reanimated';
import { useRoute } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useTranslation, getApiText } from '../shared/i18n';
import type { Order } from '../data/domains/orders';
import { formatOrderDate } from '../shared/utils/dateUtils';
import { formatCurrency, getDefaultCurrencyCode } from '../shared/utils/currencyUtils';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';

function getStatusColor(status: string, c: ThemeColors): string {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return c.success;
    case 'pending':
      return c.warning;
    case 'cancelled':
    case 'failed':
      return c.error;
    case 'processing':
      return c.alert;
    default:
      return c.textSecondary;
  }
}

function createOrderDetailsStyles(c: ThemeColors) {
  const card = {
    ...studioCardShell(c),
    borderRadius: DASH_STUDIO_RADIUS,
    padding: appTheme.spacing.lg,
  };
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    contentInner: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xxl,
      gap: STACK_SECTION_GAP,
    },
    bottomSpacer: {
      height: 40,
    },
    orderInfoCard: card,
    orderInfoHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    orderNumber: {
      ...appTheme.typography.h3,
      color: c.text,
    },
    orderDate: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: 4,
    },
    statusBadge: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: 4,
      borderRadius: appTheme.borderRadius.sm,
    },
    statusText: {
      ...appTheme.typography.bodySmall,
      fontWeight: '600',
      textTransform: 'capitalize',
    },
    section: {},
    sectionTitle: {
      ...appTheme.typography.bodySmall,
      color: c.textLight,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: appTheme.spacing.sm,
      fontWeight: '600',
    },
    addressCard: {
      ...card,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.sm,
    },
    addressInfo: {
      flex: 1,
    },
    addressName: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
    },
    addressText: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: 4,
    },
    itemsCard: {
      ...card,
      padding: 0,
      overflow: 'hidden',
    },
    itemRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: appTheme.spacing.md,
    },
    itemRowBorder: {
      borderTopWidth: 1,
      borderTopColor: c.border,
    },
    itemInfo: {
      flex: 1,
      marginRight: appTheme.spacing.md,
    },
    itemName: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '500',
    },
    itemOptions: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: 2,
    },
    itemQty: {
      ...appTheme.typography.bodySmall,
      color: c.textLight,
      marginTop: 4,
    },
    itemPrice: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
    },
    summaryCard: card,
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: appTheme.spacing.sm,
    },
    summaryLabel: {
      ...appTheme.typography.body,
      color: c.textSecondary,
    },
    summaryValue: {
      ...appTheme.typography.body,
      color: c.text,
    },
    discountText: {
      color: c.success,
    },
    divider: {
      height: 1,
      backgroundColor: c.border,
      marginVertical: appTheme.spacing.sm,
    },
    totalLabel: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
    },
    totalValue: {
      ...appTheme.typography.h3,
      color: c.primary,
    },
    paymentCard: {
      ...card,
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    paymentText: {
      ...appTheme.typography.body,
      color: c.text,
      textTransform: 'capitalize',
    },
    reorderButtonWrap: {},
    reorderButton: {
      backgroundColor: c.primary,
      marginHorizontal: appTheme.spacing.md,
      marginTop: appTheme.spacing.xl,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      ...appTheme.shadows.sm,
      shadowColor: c.shadowColor,
    },
    reorderButtonText: {
      ...appTheme.typography.body,
      color: c.onPrimary,
      fontWeight: '600',
    },
  });
}

function getDisplayStatus(order: Order): string {
  const payment = (order.payment_status ?? '').toLowerCase();
  if (payment === 'failed') {return 'failed';}
  return (order.status ?? 'pending').toLowerCase();
}

export const OrderDetailsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const online_orders_enabled = useRequireOnlineOrdersEnabled();
  const route = useRoute();
  const { t } = useTranslation();
  const order: Order = (route.params as any)?.order || {};
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderDetailsStyles(themeColors), [themeColors]);
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const status = getDisplayStatus(order);
  const status_color = getStatusColor(status, themeColors);

  const handleReorder = useCallback(() => {
    Alert.alert(t('order.reorderConfirmTitle'), t('order.reorderConfirmMessage'), [
      { text: t('order.cancel'), style: 'cancel' },
      {
        text: t('order.reorder'),
        onPress: () => {
          navigation.navigate('Checkout', { plan: order, isReorder: true });
        },
      },
    ]);
  }, [navigation, order, t]);

  const subtotal = useMemo(
    () => (order.items ?? []).reduce((sum: number, item: any) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0),
    [order.items],
  );
  const delivery_fee = order.delivery_fee ?? 0;
  const discount = order.discount ?? 0;
  const total = typeof order.total === 'number' ? order.total : Number(order.total) || 0;
  const currency = order.currency ?? getDefaultCurrencyCode();
  const address_line = order.address?.full_address ?? order.address?.address ?? '';

  const insets = useSafeAreaInsets();
  const content_top = androidAppHeaderContentTop(insets.top, appTheme.spacing.sm);

  if (!online_orders_enabled) {
    return null;
  }

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader title={t('order.detailsTitle')} onBack={on_back_exit} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.contentInner, { paddingTop: content_top, paddingBottom: insets.bottom + appTheme.spacing.xxl }]}
        keyboardShouldPersistTaps="handled">
        <View style={styles.orderInfoCard}>
          <View style={styles.orderInfoHeader}>
            <View>
              <Text style={styles.orderNumber}>{t('order.orderNumber')}{order.id}</Text>
              <Text style={styles.orderDate}>{formatOrderDate(order.date ?? order.created_at)}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: status_color + '18' }]}>
              <Text style={[styles.statusText, { color: status_color }]}>{status}</Text>
            </View>
          </View>
        </View>

        {order.address && address_line ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('order.deliveryAddress')}</Text>
            <View style={styles.addressCard}>
              <SvgIcon name="map" size={20} color={themeColors.primary} />
              <View style={styles.addressInfo}>
                <Text style={styles.addressName}>{order.address.name || t('order.deliveryAddress')}</Text>
                <Text style={styles.addressText}>{address_line}</Text>
              </View>
            </View>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('order.orderItems')}</Text>
          <View style={styles.itemsCard}>
            {(order.items ?? []).map((item: any, index: number) => (
              <View key={item.id ?? index} style={[styles.itemRow, index > 0 && styles.itemRowBorder]}>
                <View style={styles.itemInfo}>
                  <Text style={styles.itemName}>
                    {getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name || item.title || '—'}
                  </Text>
                  {item.options ? <Text style={styles.itemOptions}>{item.options}</Text> : null}
                  <Text style={styles.itemQty}>{t('order.qty')}: {item.quantity ?? 1}</Text>
                </View>
                <Text style={styles.itemPrice}>
                  {formatCurrency((item.price ?? 0) * (item.quantity ?? 1), { currency })}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('order.paymentSummary')}</Text>
          <View style={styles.summaryCard}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>{t('order.subtotal')}</Text>
              <Text style={styles.summaryValue}>{formatCurrency(subtotal, { currency })}</Text>
            </View>
            {delivery_fee > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>{t('order.deliveryFee')}</Text>
                <Text style={styles.summaryValue}>{formatCurrency(delivery_fee, { currency })}</Text>
              </View>
            )}
            {discount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>{t('order.discount')}</Text>
                <Text style={[styles.summaryValue, styles.discountText]}>-{formatCurrency(discount, { currency })}</Text>
              </View>
            )}
            <View style={styles.divider} />
            <View style={styles.summaryRow}>
              <Text style={styles.totalLabel}>{t('order.total')}</Text>
              <Text style={styles.totalValue}>{formatCurrency(total, { currency })}</Text>
            </View>
          </View>
        </View>

        {order.payment_method ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('order.paymentMethod')}</Text>
            <View style={styles.paymentCard}>
              <SvgIcon name="card" size={20} color={themeColors.primary} />
              <Text style={styles.paymentText}>{order.payment_method}</Text>
            </View>
          </View>
        ) : null}

        <Clickable onPress={handleReorder} accessibilityLabel={t('order.reorder')} accessibilityRole="button">
          <View style={styles.reorderButtonWrap}>
            <View style={styles.reorderButton}>
            <SvgIcon name="reload" size={20} color={themeColors.onPrimary} />
            <Text style={styles.reorderButtonText}>{t('order.reorder')}</Text>
            </View>
          </View>
        </Clickable>

        <View style={styles.bottomSpacer} />
      </Animated.ScrollView>
    </StackScreenShell>
  );
};
